import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Building2, Loader2, Package, Gamepad2, Settings2, Truck } from 'lucide-react';
import api from '../lib/api';
import RetailerLayout from '../components/RetailerLayout';
import CmsBlocksRenderer from '../components/CmsBlocksRenderer';
import useListingCmsBlocks from '../hooks/useListingCmsBlocks';
import PartnerBadges, { RosetteIcon } from '../components/PartnerBadges';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { isPartnerClickable, PARTNER_DISABLED_TOOLTIP, getPartnerAccessLabel, PARTNER_LABEL_INACTIVE } from '../lib/partnerPolicy';

/**
 * PartnersListPage — Page listing hybride (data-first + CMS overlay)
 * 
 * Route: /partners
 * 
 * ARCHITECTURE HYBRIDE:
 * ┌─────────────────────────────────┐
 * │  CMS TOP (bannières, éditorial) │ ← optionnel, zone "header"
 * ├─────────────────────────────────┤
 * │  LISTING HEADER (titre, search) │ ← React hardcodé
 * │  FILTRES (type + partenaire)    │ ← React + state + API
 * │  GRID PARTNERS (API /partners)  │ ← données métier
 * ├─────────────────────────────────┤
 * │  CMS BOTTOM (CTA, marketing)    │ ← optionnel, zone "footer"
 * └─────────────────────────────────┘
 * 
 * Règle : si CMS vide → page identique à avant. Zéro impact métier.
 */

const TYPE_FILTERS = [
  { value: 'all', label: 'Tous', icon: null },
  { value: 'distributor', label: 'Distributeurs', icon: Truck },
  { value: 'publisher', label: 'Éditeurs', icon: Gamepad2 },
  { value: 'other', label: 'Autres', icon: Settings2 }
];

const PARTNER_FILTERS = [
  { value: 'all', label: 'Tous' },
  { value: 'partner', label: 'Partenaires uniquement' }
];

const PartnersListPage = () => {
  const [partners, setPartners] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filtering, setFiltering] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');

  // CMS overlay — chargement silencieux des blocs éditoriaux
  const { blocksByZone, hasCmsContent } = useListingCmsBlocks('page-partners');

  // Charger les partenaires via API avec filtres serveur
  const loadPartners = useCallback(async (isInitial = false) => {
    try {
      if (isInitial) setInitialLoading(true);
      else setFiltering(true);

      const params = new URLSearchParams({ is_active: 'true' });

      // Filtre type côté API
      if (typeFilter !== 'all') {
        params.set('type', typeFilter);
      }
      // Filtre partenaire commercial côté API
      if (partnerFilter === 'partner') {
        params.set('is_commercial_partner', 'true');
      }

      const response = await api.get(`/partners?${params.toString()}`);
      setPartners(response.data || []);
    } catch (error) {
      console.error('Erreur chargement partenaires:', error);
    } finally {
      setInitialLoading(false);
      setFiltering(false);
    }
  }, [typeFilter, partnerFilter]);

  // Chargement initial
  useEffect(() => {
    document.title = 'Nos Partenaires - Games Visor';
    loadPartners(true);
    return () => { document.title = 'Games Visor'; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rechargement sur changement de filtre (pas de full-page spinner)
  useEffect(() => {
    if (!initialLoading) {
      loadPartners(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, partnerFilter]);

  // Recherche texte locale (instantanée, pas d'appel API)
  const displayedPartners = searchQuery
    ? partners.filter(p => {
        const q = searchQuery.toLowerCase();
        return (p.nom || '').toLowerCase().includes(q) ||
               (p.description || '').toLowerCase().includes(q);
      })
    : partners;

  if (initialLoading) {
    return (
      <RetailerLayout>
        <div className="min-h-screen flex items-center justify-center bg-[#F7F5F2]">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Chargement des partenaires...</p>
          </div>
        </div>
      </RetailerLayout>
    );
  }

  return (
    <RetailerLayout>
      <div className="min-h-screen bg-[#F7F5F2]">

        {/* ══════ CMS ZONE TOP — Bannières, texte éditorial, vitrines ══════ */}
        {blocksByZone.header.length > 0 && (
          <CmsBlocksRenderer
            blocks={blocksByZone.header}
            zone="header"
            pageType="listing"
          />
        )}

        {/* ══════ LISTING HEADER (titre + compteur) ══════ */}
        <div className="bg-white border-b shadow-sm">
          <div className="max-w-[1200px] mx-auto px-6 sm:px-6 py-6 sm:py-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3" data-testid="partners-list-title">
              <Building2 className="h-7 w-7 text-blue-600" />
              Nos Partenaires
            </h1>
            <p className="text-gray-600 mt-1" data-testid="partners-count">
              {displayedPartners.length} partenaire{displayedPartners.length !== 1 ? 's' : ''} 
              {(typeFilter !== 'all' || partnerFilter !== 'all' || searchQuery) ? ' trouvé' + (displayedPartners.length !== 1 ? 's' : '') : ' disponible' + (displayedPartners.length !== 1 ? 's' : '')}
            </p>
          </div>
        </div>

        {/* Filtres */}
        <div className="max-w-[1200px] mx-auto px-6 sm:px-6 pt-6">
          {/* Barre de recherche */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Rechercher un partenaire..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white"
              data-testid="search-partners-retailer"
            />
          </div>

          {/* Filtres type + partenaire — responsive stack */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {/* Filtre type fournisseur */}
            <div className="flex gap-2 flex-wrap" data-testid="type-filter-retailer">
              {TYPE_FILTERS.map(({ value, label, icon: Icon }) => (
                <Button
                  key={value}
                  variant={typeFilter === value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTypeFilter(value)}
                  className="text-xs sm:text-sm"
                  data-testid={`filter-type-${value}`}
                >
                  {Icon && <Icon className="h-3.5 w-3.5 mr-1.5" />}
                  {label}
                </Button>
              ))}
            </div>

            {/* Filtre statut partenaire commercial */}
            <div className="flex gap-2 flex-wrap" data-testid="partner-filter-retailer">
              {PARTNER_FILTERS.map(({ value, label }) => (
                <Button
                  key={value}
                  variant={partnerFilter === value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPartnerFilter(value)}
                  className="text-xs sm:text-sm"
                  data-testid={`filter-partner-${value}`}
                >
                  {value === 'partner' && <RosetteIcon className="w-4 h-4 mr-1" />}
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Grille de partenaires */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Spinner inline pendant le filtrage (filtres restent visibles) */}
          {filtering && (
            <div className="flex items-center justify-center py-8 mb-4" data-testid="filter-loading">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-2" />
              <span className="text-sm text-gray-500">Mise à jour...</span>
            </div>
          )}

          {!filtering && displayedPartners.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Building2 className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h2 className="text-xl font-semibold text-gray-700 mb-2">
                Aucun partenaire trouvé
              </h2>
              <p className="text-gray-500 mb-4">
                {searchQuery ? 'Essayez une autre recherche.' : 'Aucun partenaire ne correspond aux filtres sélectionnés.'}
              </p>
              {(typeFilter !== 'all' || partnerFilter !== 'all') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setTypeFilter('all'); setPartnerFilter('all'); setSearchQuery(''); }}
                  data-testid="reset-filters-btn"
                >
                  Réinitialiser les filtres
                </Button>
              )}
            </div>
          ) : !filtering && (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              data-testid="partners-grid"
            >
              {displayedPartners.map((partner) => (
                <PartnerListCard key={partner.id} partner={partner} />
              ))}
            </div>
          )}
        </div>

        {/* ══════ CMS ZONE BOTTOM — CTA, blocs marketing, éditorial ══════ */}
        {blocksByZone.footer.length > 0 && (
          <CmsBlocksRenderer
            blocks={blocksByZone.footer}
            zone="footer"
            pageType="listing"
          />
        )}

      </div>
    </RetailerLayout>
  );
};

/**
 * PartnerListCard — Carte partenaire dans la liste retailer
 */
const PartnerListCard = ({ partner }) => {
  const displayName = partner.nom || partner.name || 'Partenaire';
  const slug = partner.slug || partner.id;
  const clickable = isPartnerClickable(partner);

  const cardContent = (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden transition-all duration-200 ${clickable ? 'group-hover:shadow-md group-hover:scale-[1.01] group-focus-within:ring-2 group-focus-within:ring-blue-500' : 'opacity-75'}`}>
        {/* Logo zone — rosette en overlay top-right */}
        <div className="relative h-36 sm:h-40 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-5 border-b border-gray-100">
          {/* Badge partenaire commercial — overlay top-right, taille valorisante */}
          {partner.is_partner === true && (
            <div
              className="absolute top-2 right-2 z-10 drop-shadow-sm"
              title="Partenaire commercial"
              data-testid={`rosette-${partner.id}`}
            >
              <RosetteIcon className="w-7 h-7 sm:w-9 sm:h-9" />
            </div>
          )}
          {partner.logo ? (
            <img
              src={partner.logo}
              alt={`Logo ${displayName}`}
              className="max-w-full max-h-full object-contain"
              style={{ maxWidth: '70%', maxHeight: '90px' }}
              loading="lazy"
            />
          ) : (
            <Building2 className="h-12 w-12 text-gray-300" />
          )}
        </div>

        {/* Info zone — type badge uniquement, PAS de rosette ici */}
        <div className="p-4">
          <h3 className={`font-semibold text-lg mb-2 transition-colors ${clickable ? 'text-gray-900 group-hover:text-blue-600' : 'text-gray-500'}`}>
            {displayName}
          </h3>

          {/* Badge type fournisseur uniquement (rosette exclue — elle est dans la zone logo) */}
          <PartnerBadges
            supplierType={partner.supplier_type || 'other'}
            capabilities={partner.capabilities}
            isPartner={false}
            size="sm"
            maxBadges={2}
            className="mb-3"
          />

          {/* Infos commerciales (condensées — structured data) */}
          <div className="flex flex-wrap gap-2 text-xs text-gray-500">
            {(() => {
              const rules = partner.shipping_rules || [];
              const defaultRule = rules.find(r => r.zone === 'france_metropolitaine') || rules[0];
              const francoVal = defaultRule?.franco ?? partner.franco;
              return francoVal != null ? (
                <span className="bg-gray-50 border border-gray-200 rounded px-2 py-0.5">
                  Franco: {francoVal}&euro;
                </span>
              ) : null;
            })()}
            {partner.minimum_commande && (
              <span className="bg-gray-50 border border-gray-200 rounded px-2 py-0.5">
                Min: {partner.minimum_commande}&euro;
              </span>
            )}
            {(partner.shipping_rules || []).length > 0 && (
              <span className="bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5 text-indigo-600">
                {(partner.shipping_rules || []).length} zone{(partner.shipping_rules || []).length > 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* CTA */}
          <div className={`mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-sm font-medium ${clickable ? 'text-blue-600 group-hover:text-blue-700' : 'text-gray-400'}`}>
            <Package className="h-4 w-4" />
            {clickable ? 'Voir le catalogue' : PARTNER_LABEL_INACTIVE}
          </div>
        </div>
      </div>
  );

  if (clickable) {
    return (
      <Link
        to={`/partner/${slug}`}
        className="block group focus:outline-none"
        data-testid={`partner-list-card-${partner.id}`}
      >
        {cardContent}
      </Link>
    );
  }

  return (
    <div
      className="block"
      aria-disabled="true"
      title={PARTNER_DISABLED_TOOLTIP}
      data-testid={`partner-list-card-${partner.id}`}
    >
      {cardContent}
    </div>
  );
};

export default PartnersListPage;
